import { CatalogRoute } from 'src/catalog/routes/CatalogRoute'
import { NavItemType } from '@sendoutcards/quantum-design-ui'
import { CampaignsRoute } from 'src/campaigns/routes/CampaignsRoute'
import { RelationshipManagerRoute } from 'src/contacts/routes/RelationshipManagerRoute'
import { GiftStoreRoute } from 'src/gift_store/routes/GiftStoreRoute'
import { MyCampaignsRoute } from 'src/campaigns/routes/MyCampaignsRoute'
import { PurchasedCampaignsRoute } from 'src/campaigns/routes/PurchasedCampaignsRoute'
import { useActions, usePersistedUserData, useQueries } from 'src/hooks'
import { ImportContactsRoute } from 'src/contacts/routes/ImportContactsRoute'
import { BaseRoute } from 'src/routes/BaseRoute'
import { FavoriteCardsRoute } from 'src/catalog/routes/FavoriteCardsRoute'
import { CustomCardRoute } from 'src/catalog/routes/CustomCardRoute'
import { PhotoDropCardsRoute } from 'src/catalog/routes/PhotoDropCardsRoute'
import { CardCategoryRoute } from 'src/catalog/routes/CardCategoryRoute'
import { CardCollectionRoute } from 'src/catalog/routes/CardCollectionRoute'
import { CreateCampaignRoute } from 'src/campaigns/routes/CreateCampaignRoute'
import { CampaignStoreRoute } from 'src/campaign_store/routes/CampaignStoreRoute'
import {
  getCardCategories,
  getCardCollections,
  getGiftCategories,
  getItemCategories,
} from 'src/graphql'
import { GiftCategoryRoute } from 'src/gift_store/routes/GiftCategoryRoute'
import { GroupsRoute } from 'src/contacts/routes/GroupsRoute'

export const useNavSections = (
  route: BaseRoute,
  isEnabled: boolean,
): NavItemType[] => {
  // TODO: Provide mechanism to cache queries locally.
  // For now only load these resources as needed.
  const [
    cardCategoryFragments,
    cardCollectionFragments,
    giftCategories,
    campaignCategories,
  ] = useQueries(
    route.subroute.path === CatalogRoute.path && isEnabled
      ? getCardCategories()
      : undefined,
    route.subroute.path === CatalogRoute.path && isEnabled
      ? getCardCollections()
      : undefined,
    route.subroute.path === GiftStoreRoute.path && isEnabled
      ? getGiftCategories()
      : undefined,
    route.subroute.path === CampaignsRoute.path && isEnabled
      ? getItemCategories()
      : undefined,
  )
  const { controlData } = usePersistedUserData()

  const cardCategories =
    cardCategoryFragments?.map(category => ({
      title: category.description,
      isActive:
        route.subroute.path === CatalogRoute.path &&
        route.subroute.subroute?.path === CardCategoryRoute.path &&
        route.subroute.subroute?.cardCategoryId === category.id,
      onClick: () => actions.selectedCardCategory(category.id),
    })) ?? []

  const cardCollections =
    cardCollectionFragments?.map(collection => ({
      title: collection.title,
      isActive:
        route.subroute.path === CatalogRoute.path &&
        route.subroute.subroute?.path === CardCollectionRoute.path &&
        route.subroute.subroute?.cardCollectionId === collection.id,
      onClick: () => actions.selectedCardCollection(collection.id),
    })) ?? []

  const isBlankCardModalOpen = !!controlData.isBlankCardModalOpen
  const actions = useActions()
  const giftStoreSubNavIcons = [
    'gift' as 'gift',
    'priceTag' as 'priceTag',
    'cart' as 'cart',
  ]

  if (!isEnabled) {
    return []
  }

  return [
    {
      title: 'Catalog',
      icon: 'catalog',
      onClick: actions.openCatalog,
      isActive: route.subroute.path === CatalogRoute.path,
      subNavigation: [
        {
          title: 'Filter By',
          isActive:
            route.subroute.path === CatalogRoute.path &&
            (route.subroute.subroute?.path === FavoriteCardsRoute.path ||
              route.subroute.subroute?.path === PhotoDropCardsRoute.path),
          icon: 'filterBy',
          links: [
            {
              title: 'Create your own Card',
              isActive:
                route.subroute.path === CatalogRoute.path &&
                isBlankCardModalOpen,
              onClick: () => actions.toggleIsBlankCardModalOpen(),
            },
            {
              title: 'Show Catalog Cards',
              isActive:
                route.subroute.path === CatalogRoute.path &&
                route.subroute.subroute?.path === CustomCardRoute.path,
              onClick: actions.selectedStandardCards,
            },
            {
              title: 'Show Photo Drop Cards',
              isActive:
                route.subroute.path === CatalogRoute.path &&
                route.subroute.subroute?.path === PhotoDropCardsRoute.path,
              onClick: actions.selectedPhotoDropCards,
            },
          ],
        },
        {
          title: 'Collections',
          isActive:
            route.subroute.path === CatalogRoute.path &&
            route.subroute.subroute?.path === CardCollectionRoute.path,
          icon: 'catalog',
          links: cardCollections,
        },
        {
          title: 'Categories',
          isActive:
            route.subroute.path === CatalogRoute.path &&
            route.subroute.subroute?.path === CardCategoryRoute.path,
          icon: 'layer',
          links: cardCategories,
        },
      ],
    },
    {
      title: 'Campaigns',
      icon: 'campaigns',
      onClick: actions.openCampaigns,
      isActive: route.subroute.path === CampaignsRoute.path,
      subNavigation: [
        {
          title: 'Campaigns Actions',
          isActive:
            route.subroute.path === CampaignsRoute.path &&
            (route.subroute.subroute?.path === CreateCampaignRoute.path ||
              route.subroute.subroute?.path === MyCampaignsRoute.path),
          icon: 'star',
          links: [
            {
              title: 'Create a new campaign',
              isActive:
                route.subroute.path === CampaignsRoute.path &&
                route.subroute.subroute?.path === CreateCampaignRoute.path,
              onClick: actions.openCreateCampaign,
            },
            {
              title: 'View my campaigns',
              isActive:
                route.subroute.path === CampaignsRoute.path &&
                route.subroute.subroute?.path === MyCampaignsRoute.path,
              onClick: () => actions.openCampaigns(MyCampaignsRoute()),
            },
          ],
        },
        {
          title: 'Campaign Store',
          isActive:
            route.subroute.path === CampaignsRoute.path &&
            (route.subroute.subroute?.path === CampaignStoreRoute.path ||
              route.subroute.subroute?.path === PurchasedCampaignsRoute.path),
          icon: 'cart',
          links: [
            {
              title: 'View purchased campaigns',
              isActive:
                route.subroute.path === CampaignsRoute.path &&
                route.subroute.subroute?.path === PurchasedCampaignsRoute.path,
              onClick: () => actions.openCampaigns(PurchasedCampaignsRoute()),
            },
            ...(campaignCategories ?? []).map(category => ({
              title: category.name,
              isActive:
                route.subroute.path === CampaignsRoute.path &&
                route.subroute.subroute?.path === CampaignStoreRoute.path &&
                route.subroute.subroute?.categoryId === category.id,
              onClick: () => actions.openCampaignStore(category.id),
            })),
          ],
        },
      ],
    },
    {
      title: 'Contact',
      icon: 'contact',
      onClick: actions.openRelationshipManager,
      isActive:
        route.subroute.path === RelationshipManagerRoute.path ||
        route.subroute.path === ImportContactsRoute.path ||
        route.subroute.path === GroupsRoute.path,
      subNavigation: [
        {
          title: 'Contacts',
          isActive:
            route.subroute.path === RelationshipManagerRoute.path ||
            route.subroute.path === ImportContactsRoute.path,
          icon: 'contact',
          links: [
            {
              title: 'View contacts',
              isActive: route.subroute.path === RelationshipManagerRoute.path,
              onClick: () => actions.openRelationshipManager(),
            },
            {
              title: 'Create new contact',
              isActive:
                route.subroute.path === RelationshipManagerRoute.path &&
                route.subroute.isNewContactModalOpen,
              onClick: () => {
                if (route.subroute.path === RelationshipManagerRoute.path) {
                  actions.toggleIsNewContactModalOpen()
                } else {
                  actions.openRelationshipManager()
                  actions.toggleIsNewContactModalOpen()
                }
              },
            },
            {
              title: 'Import contacts',
              isActive: route.subroute.path === ImportContactsRoute.path,
              onClick: actions.openContactsImporter,
            },
          ],
        },
        {
          title: 'Groups',
          isActive: route.subroute.path === GroupsRoute.path,
          icon: 'multipleUsers3x',
          links: [
            {
              title: 'View my groups',
              isActive: route.subroute.path === GroupsRoute.path,
              onClick: actions.openGroups,
            },
            {
              title: 'Create new group',
              isActive:
                route.subroute.path === GroupsRoute.path &&
                route.subroute.isNewGroupModalOpen,
              onClick: () => {
                if (route.subroute.path !== GroupsRoute.path) {
                  actions.openGroups()
                  actions.toggleIsNewGroupModalOpen()
                } else {
                  actions.toggleIsNewGroupModalOpen()
                }
              },
            },
          ],
        },
      ],
    },
    {
      title: 'Gifts',
      icon: 'gift',
      onClick: actions.openGiftStore,
      isActive: route.subroute.path === GiftStoreRoute.path,
      subNavigation: (giftCategories ?? []).map((category, index) => ({
        title: category.name,
        icon: giftStoreSubNavIcons[index],
        isActive:
          route.subroute.path === GiftStoreRoute.path &&
          route.subroute.highLevelCategory === category.id,
        links: category.children.map(child => ({
          title: child.name,
          isActive:
            route.subroute.path === GiftStoreRoute.path &&
            route.subroute.subroute?.path === GiftCategoryRoute.path &&
            route.subroute.subroute.giftCategoryId === child.id,
          onClick: () =>
            actions.openGiftStore(GiftCategoryRoute(child.id), category.id),
        })),
      })),
    },
  ]
}
